import { Grid, Typography, useTheme } from '@mui/material';
import React, { useMemo } from 'react';
import {
  formatCurrency,
  getCancellationPolicyDistribution,
  getHeadingStyle,
  getSupplyByPropertyType,
} from 'src/utils/functions';
import {
  COLOR_PRIMARY_1,
  COLOR_PRIMARY_1_400,
  COLOR_PRIMARY_1_500,
  COLOR_PRIMARY_1_600,
  COLOR_PRIMARY_1_700,
  COLOR_PRIMARY_1_800,
  COLOR_TERTIARY_1_100,
  COLOR_TERTIARY_1_200,
  COLOR_TERTIARY_1_300,
  COLOR_TERTIARY_1_400,
  COLOR_TERTIARY_1_500,
  COLOR_TEXT_2,
  GRAPH_BLUE,
} from 'src/utils/colors';
import { DynamicPieChart } from 'src/App/Dashboards/Market/Components/Charts';
import PieDataCard from 'src/App/Dashboards/Market/Components/Cards/PieDataCard';
import { VisibleListingsProp } from 'src/interfaces/iproptypes';
import { PieChartData } from 'src/interfaces/idashboard';
import { LoadingCard } from 'src/App/Dashboards/Market/Components';
import NoDataCard from 'src/App/Dashboards/Market/Components/Cards/NoDataCard';

const PropertyTypeChannelPieChart = ({
  cachedData,
  viewMode,
  isDynamicLoading,
  multipleMarkersCallLoading,
  noDataInMarket,
  totalListingsCount,
  listingByPropertyTypeGraphRef,
  cancellationPolicyGraphRef,
}: {
  cachedData: {
    property_type?: { [key: string]: number } | null;
    cancellation_policy?: { [key: string]: number } | null;
    channel_type?: { [key: string]: number } | null;
  } | null;
  viewMode: 'map' | 'dashboard' | 'discovery' | 'compare' | 'advance' | 'searchListing';
  isDynamicLoading: boolean;
  multipleMarkersCallLoading: Boolean;
  noDataInMarket: boolean;
  totalListingsCount: number;
  listingByPropertyTypeGraphRef: any;
  cancellationPolicyGraphRef: any;
}) => {
  const theme = useTheme();

  const isDataStaticPropertyType =
    viewMode === 'dashboard' && cachedData && cachedData.property_type;
  const isDataLoadingPropertyType = multipleMarkersCallLoading && !isDataStaticPropertyType;

  const supplyByPropertyType = useMemo(() => {
    if (isDataLoadingPropertyType) return [];
    return getSupplyByPropertyType((cachedData?.property_type ?? {}) as { [key: string]: number });
  }, [cachedData?.property_type, isDataLoadingPropertyType]);

  const isDataStaticChannelType = viewMode === 'dashboard' && cachedData;

  const isDataStaticCancellationPolicy =
    viewMode === 'dashboard' && cachedData && cachedData.cancellation_policy;
  const isDataLoadingCancellationPolicy = !isDataStaticCancellationPolicy;

  const cancellationPolicyDistribution = useMemo(() => {
    if (isDataLoadingCancellationPolicy) {
      return [];
    }
    return getCancellationPolicyDistribution(cachedData?.cancellation_policy);
  }, [viewMode, cachedData]);

  const normalizedCancellationPolicyDistribution = useMemo(() => {
    let newCancellationPolicyDistribution: ({ exactValue: number } & PieChartData)[] = [];
    const totalPercentage = cancellationPolicyDistribution.reduce(
      (acc, curr) => acc + curr.value,
      0
    );

    // Actual percentage value
    newCancellationPolicyDistribution = cancellationPolicyDistribution.map(item => ({
      ...item,
      value: 0,
      exactValue: (item.value / Math.max(totalPercentage, 1)) * 100,
    }));

    // Getting the floored value
    let flooredCancellationPolicyDistribution = newCancellationPolicyDistribution.map(item => ({
      ...item,
      value: Math.floor(item.exactValue),
    }));

    // Difference between the 100% and the sum of the floored values
    const diff =
      100 - flooredCancellationPolicyDistribution.reduce((acc, curr) => acc + curr.value, 0);

    if (diff === 0) {
      return flooredCancellationPolicyDistribution;
    }

    // Increasing the % value of the items with the largest remainder
    flooredCancellationPolicyDistribution
      .sort((a, b) => (b.exactValue % 1) - (a.exactValue % 1)) // sort by largest remainder
      .slice(0, diff) // pick top diff: for eg. if the diff is 2, then increase the % value of the first 2 by 1
      .forEach(item => item.value++);

    return flooredCancellationPolicyDistribution;
  }, [cancellationPolicyDistribution]);

  const getCancellationPolicyTooltipText = (value: number) => {
    const numberOfListings = formatCurrency(String(Math.round((value / 100) * totalListingsCount)));
    return `${numberOfListings} listings ( ${value}% )`;
  };

  const pieChartRadius: number = 60;

  return (
    <>
      <Grid item container>
        {noDataInMarket ? (
          <NoDataCard height={150} borderRadius={8} />
        ) : (isDataLoadingPropertyType && multipleMarkersCallLoading) || isDynamicLoading ? (
          <LoadingCard height={150} type="bars" />
        ) : (
          <Grid
            container
            direction="column"
            justifyContent="center"
            item
            xs={12}
            // spacing={1}
            sx={{
              boxShadow: '0 4px 10px #0003',
              borderRadius: 1,
              ...theme.utils.cardPadding,
              height: '250px',
            }}
          >
            <Grid item container sx={{ width: '100%' }}>
              <Grid item xs={12} ref={listingByPropertyTypeGraphRef}>
                <Typography variant="body1" sx={getHeadingStyle(COLOR_TEXT_2)}>
                  Number of Listings by Property Type
                </Typography>

                <DynamicPieChart
                  data={supplyByPropertyType}
                  unit={'listings'}
                  pieColors={[
                    COLOR_PRIMARY_1_800,
                    COLOR_PRIMARY_1_700,
                    COLOR_PRIMARY_1_600,
                    COLOR_PRIMARY_1_500,
                    COLOR_PRIMARY_1_400,
                  ]}
                  pieAccentColor={COLOR_PRIMARY_1}
                  label={'Property Type'}
                  radius={pieChartRadius}
                  parent="listing_by_property_type"
                />
              </Grid>
            </Grid>
          </Grid>
        )}
      </Grid>

      <Grid item container>
        {noDataInMarket ? (
          <NoDataCard height={150} borderRadius={8} />
        ) : (isDataLoadingCancellationPolicy && multipleMarkersCallLoading) || isDynamicLoading ? (
          <LoadingCard height={150} type="bars" />
        ) : (
          <Grid
            container
            direction="column"
            justifyContent="center"
            item
            xs={12}
            // spacing={1}
            sx={{
              boxShadow: '0 4px 10px #0003',
              borderRadius: 1,
              ...theme.utils.cardPadding,
            }}
          >
            <Grid item container sx={{ width: '100%' }}>
              <Grid item xs={12} ref={cancellationPolicyGraphRef}>
                <Typography variant="body1" sx={getHeadingStyle(COLOR_TEXT_2)}>
                  Number of Listings by Cancellation Policy Type
                </Typography>

                <PieDataCard
                  tooltipContent="Market cancellation policies"
                  data={normalizedCancellationPolicyDistribution}
                  unit="%"
                  pieColors={[GRAPH_BLUE, GRAPH_BLUE, GRAPH_BLUE]}
                  pieAccentColor={GRAPH_BLUE + 'CC'}
                  label={'Cancellation Policy'}
                  radius={pieChartRadius}
                  getTooltipText={getCancellationPolicyTooltipText}
                  parent="listing_by_cancellation_policy"
                />
              </Grid>
            </Grid>
          </Grid>
        )}
      </Grid>
    </>
  );
};

export default React.memo(PropertyTypeChannelPieChart);
